import { z } from 'zod';

export interface LegalDocumentRenderInput {
    legalDocumentUuid: string;
    legalDocumentContentType: string;
    legalDocumentText: string;
}

function escapeHtmlText(value: string): string {
    return value
        .replaceAll('&', '&amp;')
        .replaceAll('<', '&lt;')
        .replaceAll('>', '&gt;')
        .replaceAll('"', '&quot;')
        .replaceAll("'", '&#39;');
}

export function buildLegalDocumentIframeSrcDoc(input: LegalDocumentRenderInput): string {
    const contentType: string = input.legalDocumentContentType;
    const legalDocumentUuid: string = input.legalDocumentUuid;
    const rawText: string = input.legalDocumentText;

    const bodyHtml: string =
        contentType === 'text/html' || contentType === 'application/xhtml+xml'
            ? rawText
            : contentType === 'text/plain'
              ? `<pre style="white-space: pre-wrap; font-family: ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, 'Liberation Mono', 'Courier New', monospace;">${escapeHtmlText(rawText)}</pre>`
              : (() => {
                    throw new Error(`Unsupported legal document content type: ${contentType}`);
                })();

    // srcDoc uses a unique opaque origin; postMessage origin will be "null".
    return `<!doctype html>
<html>
  <head>
    <meta charset="utf-8" />
    <meta name="viewport" content="width=device-width, initial-scale=1" />
    <style>
      body { margin: 0; padding: 16px; font-family: ui-sans-serif, system-ui, -apple-system, Segoe UI, Roboto, Helvetica, Arial, "Apple Color Emoji", "Segoe UI Emoji"; }
    </style>
  </head>
  <body>
    ${bodyHtml}
    <script>
      (function() {
        var legalDocumentUuid = ${JSON.stringify(legalDocumentUuid)};
        var reached = false;
        function maybeNotify() {
          if (reached) return;
          var el = document.scrollingElement || document.documentElement;
          var scrollTop = (el && el.scrollTop) ? el.scrollTop : (window.scrollY || 0);
          var viewport = window.innerHeight || (el && el.clientHeight) || 0;
          var full = (el && el.scrollHeight) || document.documentElement.scrollHeight || 0;
          if (full <= viewport + 8 || (scrollTop + viewport >= full - 8)) {
            reached = true;
            parent.postMessage({ type: 'LEGAL_DOC_REACHED_BOTTOM', legalDocumentUuid: legalDocumentUuid }, '*');
          }
        }
        window.addEventListener('scroll', maybeNotify, { passive: true });
        window.addEventListener('load', maybeNotify);
        setTimeout(maybeNotify, 250);
      })();
    </script>
  </body>
</html>`;
}

export const LegalDocReachedBottomMessageSchema = z
    .object({
        type: z.literal('LEGAL_DOC_REACHED_BOTTOM'),
        legalDocumentUuid: z.string().uuid(),
    })
    .passthrough();

export type LegalDocReachedBottomMessage = z.infer<typeof LegalDocReachedBottomMessageSchema>;
